import type { PreferencesResponse } from '../../@types/preferences';
import { AREA_OF_CONCERN } from '../../constants';
import { usePreferences } from '../../react-query/usePreferences';
import { PracticeFeatureFlagKey, useFeatureFlag } from '../../utils/user';

export const computeImagesRefetchInterval = (
  preferences: PreferencesResponse | null | undefined,
  isEmbedded: boolean,
  disablePolling: boolean,
) => {
  const imagePollingInterval = preferences?.standalonePollingInterval || 30000;

  if (imagePollingInterval < 1000 || imagePollingInterval > 600000) {
    // Don't allow less than 1s or >10min. <1s can happen if there's an off-by-1000 mistake (e.g. setting at 15)
    return 30000;
  } else if (isEmbedded && !disablePolling) {
    return 30000;
  }

  if (disablePolling) {
    // Prioritize case presentation mode setting over the others to avoid any refetches during presentations
    return undefined;
  }

  return imagePollingInterval;
};

export const useGetModelFeatureFlags = () => {
  const isPrlEnabled = !!useFeatureFlag(PracticeFeatureFlagKey.PRL_ENABLED)
    ?.value;

  const isRblEnabled = !!useFeatureFlag(PracticeFeatureFlagKey.RBL_ENABLED)
    ?.value;

  const isCalculusEnabled = !!useFeatureFlag(
    PracticeFeatureFlagKey.CALCULUS_ENABLED,
  )?.value;

  const isCariesSubclassEnabled = !!useFeatureFlag(
    PracticeFeatureFlagKey.CARIES_SUBCLASSIFICATION_ENABLED,
  )?.value;

  // Enables display of depth of caries
  const isCariesDepthEnabled = !!useFeatureFlag(
    PracticeFeatureFlagKey.CARIES_DEPTH_ENABLED,
  )?.value;

  const isVdaEnabled = true;

  const preferencesQuery = usePreferences();
  const isSegmentationEnabled = Boolean(
    preferencesQuery?.data?.segmentationEnabled,
  );

  return {
    isPrlEnabled,
    isRblEnabled,
    isCalculusEnabled,
    isCariesDepthEnabled,
    isCariesSubclassEnabled,
    isVdaEnabled,
    isAnatomyEnabled: isSegmentationEnabled,
  };
};

export const getCariesSubTypedFindings = (allCariesFindings = []) => {
  const subclassifiedCaries = allCariesFindings?.reduce(
    (subclassifiedFindings, finding) => {
      // @ts-expect-error TS(2339): Property 'depth' does not exist on type 'never'... Remove this comment to see the full error message
      if (finding?.depth === 'into_enamel') {
        subclassifiedFindings.incipients.push(finding);
        // @ts-expect-error TS(2339): Property 'isCaries' does not exist on type 'never'... Remove this comment to see the full error message
      } else if (finding.isCaries == null || finding.isCaries) {
        subclassifiedFindings.caries.push(finding);
      } else {
        subclassifiedFindings.other.push(finding);
      }
      return subclassifiedFindings;
    },
    { caries: [], incipients: [], other: [] },
  );

  const areasOfConcern = subclassifiedCaries.other?.map((finding) => ({
    // @ts-expect-error TS(2698): Spread types may only be created from object types... Remove this comment to see the full error message
    ...finding,
    type: AREA_OF_CONCERN,
  }));

  return {
    caries: subclassifiedCaries.caries,
    incipients: subclassifiedCaries.incipients,
    areasOfConcern,
  };
};

export const ALL_KEYS = [
  'R',
  'r',
  'P',
  'p',
  'e',
  'E',
  'b',
  'B',
  'c',
  'C',
  'i',
  'I',
  'z',
  'Z',
  'M',
  'm',
  '=',
  '+',
  '-',
  '_',
  'R',
  'r',
  '.',
  ',',
  's',
  'S',
  'H',
  'h',
  'A',
  'a',
  'Space',
  'ArrowRight',
  'k',
  'K',
  'ArrowLeft',
  'j',
  'J',
  '1',
  '2',
  '3',
  '4',
  '5',
  '6',
  'v',
  'V',
  't',
  'T',
];

export const KEY_MAP = {
  keyPressedPerioFindings: ['p', 'P'],
  keyPressedRestoFindings: ['r', 'R'],
  keyPressedEndoFindings: ['e', 'E'],
  keyPressedInvert: ['i', 'I'],
  keyPressedMagnify: ['z', 'Z', 'm', 'M'],
  keyPressedRotate: ['.', ','],
  keyPressedSharpen: ['S', 's', 'h', 'H'],
  keyPressedAllAnnotations: ['A', 'a'],
  keyPressedPatientView: ['V', 'v'],
  keyPressedToothParts: ['T', 't'],
  keyPressedNextImage: ['ArrowRight', 'k', 'K'],
  keyPressedPrevImage: ['ArrowLeft', 'j', 'J'],
  keyPressedWindowLevel1: ['1'],
  keyPressedWindowLevel2: ['2'],
  keyPressedWindowLevel3: ['3'],
  keyPressedWindowLevel4: ['4'],
  keyPressedWindowLevel5: ['5'],
  keyPressedWindowLevel6: ['6'],
  keyPressedWindowLevel0: ['0'],
} as const;
